import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import DNSelectionHeader from '../components/DN/DNSelectionHeader';
import '../styles/toggle.css';
import DNTabs from '../components/DN/DNTabs';
import { DNProvider, useDN } from '../contexts/DNContext';
import { useSearchParams } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';
import emitter from '../emitter';
import { _t } from '../utils/i18n';

const DEFAULT_DN_PROJECT_CONFIG = { admin_only: [] };

// Composant interne qui utilise le contexte
function DNViewerContent({ fetchWithAuth, currentUser }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [shopSigns, setShopSigns] = useState([]);
  const [selectedShopSigns, setSelectedShopSigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [projectConfig, setProjectConfig] = useState(DEFAULT_DN_PROJECT_CONFIG);
  const [projectConfigLoaded, setProjectConfigLoaded] = useState(false);
  const { fetchProducts, fetchIssues, startDate, endDate, getFormattedDates } = useDN();

  // Store initial obfuscate param in a ref to persist it
  const obfuscateRef = useRef(searchParams.has('obfuscate'));
  const obfuscate = obfuscateRef.current;

  const jailRef = useRef(searchParams.has('jail'));
  const jail = jailRef.current;

  const { track } = useAnalytics({ currentUser, project: selectedProject?.label, module: 'DN_VIEWER' });

  useEffect(() => {
    emitter.on('selectedCategory', (category) => {
      setSelectedCategories([{ value: category.name, label: category.name }]);
    });

    emitter.on('selectedShopSign', (shopSign) => {
      if (shopSign === null) {
        setSelectedShopSigns([]);
        return;
      }

      setSelectedShopSigns([{ value: shopSign.name, label: shopSign.name }]);
    });

    return () => {
      emitter.off('selectedCategory');
      emitter.off('selectedShopSign');
    };
  }, []);

  // Fetch projects et sélection initiale du projet depuis l'URL
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/dn/projects`);

        if (response.ok) {
          const projectsData = await response.json();
          const formattedProjects = projectsData.map(project => ({
            value: project.projectId,
            label: project.projectName
          }));
          setProjects(formattedProjects);

          // Auto-select initial project from URL if present
          const projectId = parseInt(searchParams.get('projectId'));

          if (projectId && !selectedProject) {
            const projectFromUrl = formattedProjects.find(p => p.value === projectId);

            if (projectFromUrl)
              setSelectedProject(projectFromUrl);
          }
        } else {
          console.error("Failed to fetch projects");
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Effet pour gérer les changements de projectId dans l'URL après le montage initial
  useEffect(() => {
    const projectId = searchParams.get('projectId');
    const currentProjectId = selectedProject?.value;

    // Ne mettre à jour que si:
    // 1. Il y a un projectId dans l'URL
    // 2. Il est différent du projet actuel
    // 3. Les projets sont déjà chargés
    if (projectId && projectId !== currentProjectId && projects.length > 0) {
      const projectFromUrl = projects.find(p => p.value === projectId);
      if (projectFromUrl) {
        setSelectedProject(projectFromUrl);
      }
    }
  }, [searchParams, projects, selectedProject]);

  // Fetch products when project changes
  useEffect(() => {
    if (selectedProject) {
      fetchProducts(selectedProject.value, fetchWithAuth, selectedShopSigns, selectedCategories);
    }
  }, [selectedProject, fetchProducts, fetchIssues, fetchWithAuth, selectedShopSigns, selectedCategories]);

  useEffect(() => {
    if (selectedProject) {
      fetchIssues(selectedProject.value, fetchWithAuth, selectedShopSigns, selectedCategories);
    }
  }, [selectedProject, fetchIssues, fetchWithAuth]);

  useEffect(() => {
    const fetchProjectConfig = async () => {
      if (!selectedProject) {
        setProjectConfig(DEFAULT_DN_PROJECT_CONFIG);
        setProjectConfigLoaded(false);
        return;
      }

      setProjectConfigLoaded(false);

      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/config`);

        if (response.ok) {
          const configData = await response.json();
          setProjectConfig(configData || DEFAULT_DN_PROJECT_CONFIG);
        } else {
          setProjectConfig(DEFAULT_DN_PROJECT_CONFIG);
        }
      } catch (error) {
        console.error('Error fetching DN project config:', error);
        setProjectConfig(DEFAULT_DN_PROJECT_CONFIG);
      } finally {
        setProjectConfigLoaded(true);
      }
    };

    fetchProjectConfig();
  }, [selectedProject, fetchWithAuth]);

  // Nouvel effet pour charger les enseignes quand le projet ou les dates changent
  useEffect(() => {
    const fetchShopSigns = async () => {
      if (!selectedProject) return;

      try {
        const { formattedStartDate, formattedEndDate } = getFormattedDates();

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/shopSigns?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const shopSignsData = await response.json();
          // Formater les enseignes pour react-select
          const formattedShopSigns = shopSignsData
            .map(({ shopSign }) => ({
              value: shopSign,
              label: shopSign
            }))
            .sort((a, b) => a.value.localeCompare(b.value));

          setShopSigns(formattedShopSigns);
          // Conserver les sélections qui existent toujours
          setSelectedShopSigns(prev =>
            prev.filter(selected =>
              formattedShopSigns.some(sign => sign.value === selected.value)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching shop signs:", error);
      }
    };

    fetchShopSigns();
  }, [selectedProject, startDate, endDate, fetchWithAuth, getFormattedDates]);

  // Nouvel effet pour charger les catégories quand le projet ou les dates changent
  useEffect(() => {
    const fetchCategories = async () => {
      if (!selectedProject) return;

      try {
        const { formattedStartDate, formattedEndDate } = getFormattedDates();

        const response = await fetchWithAuth(
          `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/categories?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
        );

        if (response.ok) {
          const categoriesData = await response.json();

          // Adapter le format pour prendre en compte la structure de l'API
          const formattedCategories = categoriesData
            .filter(item => item.category !== null && item.category !== '')
            .map(item => ({
              value: item.category,
              label: item.category
            }))
            .sort((a, b) => a.value.localeCompare(b.value));

          setCategories(formattedCategories);
          // Conserver les sélections qui existent toujours
          setSelectedCategories(prev =>
            prev.filter(selected =>
              formattedCategories.some(category => category.value === selected.value)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [selectedProject, startDate, endDate, fetchWithAuth, getFormattedDates]);

  // Handler pour les changements de projet
  const handleProjectChange = (newProject) => {
    setSelectedProject(newProject);

    // Update URL when project changes
    if (newProject) {
      searchParams.set('projectId', newProject.value);
    } else {
      // Remove projectId from URL if no project selected
      searchParams.delete('projectId');
    }
    setSearchParams(searchParams);
    // Reset shop signs selection when project changes
    setSelectedShopSigns([]);
  };

  // Handler pour les changements d'enseignes
  const handleShopSignsChange = (newShopSigns) => {
    setSelectedShopSigns(newShopSigns || []);

    track('dn_filter_shop_sign', {
      shop_sign: newShopSigns.map(sign => sign.value).sort().join(',')
    });
  };

  // Handler pour les changements de catégories
  const handleCategoriesChange = (newCategories) => {
    setSelectedCategories(newCategories || []);

    track('dn_filter_category', {
      category: newCategories.map(category => category.value).sort().join(',')
    });
  };

  return (
    <div className="container mx-auto p-4 relative">
      {notification && (
        <div className="absolute top-0 right-0 left-0 mx-auto w-3/4 -mt-4 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 px-4 py-2 rounded-md shadow-md text-sm z-50">
          <div className="flex justify-between items-center">
            <span>{notification}</span>
            <button onClick={() => setNotification(null)} className="text-yellow-700 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100">
              &times;
            </button>
          </div>
        </div>
      )}

      <DNSelectionHeader
        currentUser={currentUser}
        projects={projects}
        selectedProject={selectedProject}
        setSelectedProject={handleProjectChange}
        loading={loading}
        shopSigns={shopSigns}
        selectedShopSigns={selectedShopSigns}
        setSelectedShopSigns={handleShopSignsChange}
        categories={categories}
        selectedCategories={selectedCategories}
        setSelectedCategories={handleCategoriesChange}
        fetchWithAuth={fetchWithAuth}
        obfuscate={obfuscate}
        jail={jail}
      />

      {selectedProject && (
        <DNTabs
          currentUser={currentUser}
          selectedProject={selectedProject}
          fetchWithAuth={fetchWithAuth}
          selectedShopSigns={selectedShopSigns}
          selectedCategories={selectedCategories}
          projectConfig={projectConfig}
          projectConfigLoaded={projectConfigLoaded}
          obfuscate={obfuscate}
        />
      )}

      {!selectedProject && (
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <p className="text-gray-600 dark:text-gray-400 text-center">
            {_t('Select a project to view numerical distribution data')}
          </p>
        </div>
      )}
    </div>
  );
}

// Composant wrapper qui fournit le contexte
function DNViewer(props) {
  return (
    <DNProvider>
      <DNViewerContent {...props} />
    </DNProvider>
  );
}

export default DNViewer;
